import { useEffect, useState } from "react";
import { Badge, Button, Dialog, Field, Icon, IconButton, Input, Select, Tag } from "../../ds/DS";
import type { Tone } from "../../ds/DS";
import { api } from "../../lib/api";
import {
  fetchTaskComments,
  postTaskComment,
  type TaskComment,
  type VTask,
  type VTicket,
} from "../data";
import type { PageProps } from "../Portal";
import { id8 } from "../ticketThread";
import { relativeTime } from "../tone";
import { DialogFoot, PageHead, ScopeNote, TabBar } from "./parts";
import { firstName } from "./util";

const ACCOUNT = "Client account";
const NO_TICKET = "None";
const KIND_LABELS: Record<string, string> = {
  document: "Document request",
  action: "Action",
};
const STATE_ORDER: Record<string, number> = {
  Requested: 0,
  Submitted: 1,
  Done: 2,
  Cancelled: 3,
};
const LABELS: Record<string, string> = {
  Requested: "With client",
  Submitted: "Ready to review",
  Done: "Done",
  Cancelled: "Cancelled",
};
const taskTone = (s: string): Tone =>
  (({
    Requested: "warning",
    Submitted: "navy",
    Done: "success",
    Cancelled: "neutral",
  })[s] as Tone) ?? "neutral";

const toDateInput = (ms: number | null) => (ms ? new Date(ms).toISOString().slice(0, 10) : "");
const ticketLabel = (t: VTicket) => `#${id8(t.id)} ${t.subject}`;

// Admin: create a task, or edit one. Document requests choose single or
// multiple files when created.
function TaskDialog({
  clientId,
  contact,
  projects,
  tickets,
  defaultProjectId,
  task,
  onClose,
  onSaved,
  onDelete,
}: {
  clientId: string;
  contact: string;
  projects: PageProps["projects"];
  tickets: VTicket[];
  defaultProjectId: string | null;
  task: VTask | null;
  onClose: () => void;
  onSaved: () => void;
  onDelete: (t: VTask) => void;
}) {
  const isEdit = !!task;
  const projectLabel = (id: string | null) =>
    id ? (projects.find((p) => p.id === id)?.name ?? ACCOUNT) : ACCOUNT;
  const [f, setF] = useState(() => ({
    kind: task ? KIND_LABELS[task.kind] : "Document request",
    scope: projectLabel(task ? task.projectId : defaultProjectId),
    files: "Multiple files",
    title: task?.title ?? "",
    detail: task?.detail ?? "",
    due: toDateInput(task?.dueAt ?? null),
    ticket: (() => {
      const t = task?.ticketId ? tickets.find((x) => x.id === task.ticketId) : undefined;
      return t ? ticketLabel(t) : NO_TICKET;
    })(),
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));
  const ticketOptions = [
    NO_TICKET,
    ...tickets
      .filter((t) => t.status !== "Closed" || t.id === task?.ticketId)
      .map(ticketLabel),
  ];

  const save = async () => {
    if (!f.title.trim()) return;
    setBusy(true);
    setErr("");
    const projectId =
      f.scope === ACCOUNT ? null : (projects.find((p) => p.name === f.scope)?.id ?? null);
    const ticketId =
      f.ticket === NO_TICKET ? null : (tickets.find((t) => ticketLabel(t) === f.ticket)?.id ?? null);
    const dueAt = f.due ? Date.parse(f.due) : null;
    const kind = f.kind === "Action" ? "action" : "document";
    try {
      if (task) {
        await api.patch(`/tasks/${task.id}`, {
          title: f.title.trim(),
          detail: f.detail,
          dueAt,
          kind,
          projectId,
          ticketId,
        });
      } else {
        await api.post("/tasks", {
          clientId,
          projectId,
          kind,
          allowMultiple: f.files === "Multiple files",
          title: f.title.trim(),
          detail: f.detail,
          dueAt,
          ticketId,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save the task. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={isEdit ? "Edit task" : "New task"}
      description={`A document for ${contact} to send, or an action to confirm. They get an email.`}
      accent
      width={560}
      onClose={onClose}
      footer={
        <DialogFoot
          saveLabel={isEdit ? "Save" : "Send task"}
          busy={busy}
          invalid={!f.title.trim()}
          onCancel={onClose}
          onSave={() => void save()}
          deleteLabel={task ? "Delete task" : undefined}
          deleteWhat={task ? `“${task.title}”` : undefined}
          onDelete={task ? () => onDelete(task) : undefined}
        />
      }
    >
      <div className="stack-16">
        {err ? <div className="form-err">{err}</div> : null}
        <div className="grid-2">
          <Field label="Type">
            <Select
              options={["Document request", "Action"]}
              value={f.kind}
              onChange={(e) => set("kind", e.target.value)}
            />
          </Field>
          <Field label="Belongs to">
            <Select
              options={[ACCOUNT, ...projects.map((p) => p.name)]}
              value={f.scope}
              onChange={(e) => set("scope", e.target.value)}
            />
          </Field>
        </div>
        {!isEdit && f.kind === "Document request" ? (
          <Field label="Files" hint="How many files the client can send for this request.">
            <div className="row-8">
              {["Single file", "Multiple files"].map((opt) => (
                <Button
                  key={opt}
                  size="sm"
                  variant={f.files === opt ? "primary" : "secondary"}
                  onClick={() => set("files", opt)}
                >
                  {opt}
                </Button>
              ))}
            </div>
          </Field>
        ) : null}
        <Field label="Title" required>
          <Input
            value={f.title}
            placeholder={f.kind === "Action" ? "Confirm the new delivery address" : "Signed contract"}
            onChange={(e) => set("title", e.target.value)}
          />
        </Field>
        <Field label="Details">
          <Input
            multiline
            rows={3}
            placeholder="Why we need it and what good looks like."
            value={f.detail}
            onChange={(e) => set("detail", e.target.value)}
          />
        </Field>
        <div className="grid-2">
          <Field label="Due">
            <Input type="date" value={f.due} onChange={(e) => set("due", e.target.value)} />
          </Field>
          <Field label="Related ticket">
            <Select
              options={ticketOptions}
              value={ticketOptions.includes(f.ticket) ? f.ticket : NO_TICKET}
              onChange={(e) => set("ticket", e.target.value)}
            />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

const initials = (s: string) =>
  s
    .split(/[\s@.]+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

// The comment thread on a task, shared between the client and Agile Software.
// Loaded when a card's comments are opened.
function TaskComments({
  task,
  contact,
  onPosted,
}: {
  task: VTask;
  contact: string;
  onPosted: () => void;
}) {
  const [comments, setComments] = useState<TaskComment[] | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    fetchTaskComments(task.id)
      .then((cs) => live && setComments(cs))
      .catch(() => live && setComments([]));
    return () => {
      live = false;
    };
  }, [task.id]);

  const send = async () => {
    const text = reply.trim();
    if (!text) return;
    setBusy(true);
    try {
      await postTaskComment(task.id, text);
      setReply("");
      setComments(await fetchTaskComments(task.id));
      onPosted(); // refresh the card's comment count
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tcard-comments">
      {comments === null ? (
        <div className="hint">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="tcard-nocomments">No comments yet.</div>
      ) : (
        comments.map((c) => {
          const who = c.author_name || c.author_email || (c.side === "team" ? "Agile Software" : "Client");
          return (
            <div key={c.id} className={"tcom" + (c.side === "team" ? " is-team" : "")}>
              <span className="tcom-av mono">{initials(who)}</span>
              <div className="tcom-body">
                <div className="tcom-head">
                  <span className="tcom-who">{who}</span>
                  <span className="tcom-at mono">{relativeTime(c.created_at)}</span>
                </div>
                <div className="tcom-text">{c.body}</div>
              </div>
            </div>
          );
        })
      )}
      <Input
        multiline
        rows={2}
        placeholder={`Reply to ${contact}`}
        value={reply}
        onChange={(e) => setReply(e.target.value)}
      />
      <div className="row-6 end">
        <Button size="sm" arrow onClick={() => void send()} disabled={busy || !reply.trim()}>
          {busy ? "Sending…" : "Add comment"}
        </Button>
      </div>
    </div>
  );
}

// Admin Tasks. Clients get To do (ToDo.tsx) instead.
export function Tasks({
  client,
  projects,
  tasks,
  tickets,
  pid,
  setPid,
  openItem,
  reload,
  toast,
  projectName,
}: PageProps) {
  const inProject = pid !== "all";
  const contact = client?.contact ? firstName(client.contact) : "the client";
  const [tab, setTab] = useState<"open" | "done">("open");
  const [dialog, setDialog] = useState<{ task: VTask | null } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openComments, setOpenComments] = useState<string | null>(null);

  // A project view shows that project's tasks; the all-projects view shows
  // everything for the client: account-level tasks and every project's tasks.
  const scoped = tasks.filter((t) => (inProject ? t.projectId === pid : true));
  const isDone = (t: VTask) => t.state === "Done" || t.state === "Cancelled";
  const openRows = scoped.filter((t) => !isDone(t));
  const doneRows = scoped.filter(isDone);
  const rows = (tab === "open" ? openRows : doneRows)
    .slice()
    .sort(
      (a, b) =>
        (STATE_ORDER[a.state] ?? 99) - (STATE_ORDER[b.state] ?? 99) ||
        Number(b.overdue) - Number(a.overdue),
    );
  const byTicket = new Map(tickets.map((t) => [t.id, t]));

  const act = async (t: VTask, run: () => Promise<unknown>, msg: string) => {
    setBusyId(t.id);
    try {
      await run();
      reload();
      toast(msg);
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusyId(null);
    }
  };
  const setState = (t: VTask, state: string, msg: string) =>
    act(t, () => api.patch(`/tasks/${t.id}`, { state }), msg);
  const remind = (t: VTask) =>
    act(t, () => api.post(`/tasks/${t.id}/remind`), `Reminder sent to ${client?.contact || "the client"}.`);
  const del = async (t: VTask) => {
    await api.del(`/tasks/${t.id}`);
    setDialog(null);
    reload();
    toast("Task removed.");
  };

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="TASKS"
        title="Tasks"
        sub={`Things we need from ${client?.name ?? "the client"}: documents to send and actions to confirm.`}
        actions={
          client ? (
            <Button icon="plus" onClick={() => setDialog({ task: null })}>
              New task
            </Button>
          ) : undefined
        }
      />
      <ScopeNote pid={pid} setPid={setPid} projectName={projectName} />

      <TabBar
        items={[
          { value: "open", label: "Open", count: openRows.length },
          { value: "done", label: "Done", count: doneRows.length },
        ]}
        value={tab}
        onChange={(v) => setTab(v as "open" | "done")}
      />

      {rows.length === 0 ? (
        <div className="list-card">
          <div className="list-blank">
            <div className="list-blank-title">
              {tab === "open" ? "Nothing open" : "Nothing done yet"}
            </div>
            <div className="list-blank-line">
              {tab === "open"
                ? "When we need a document or a decision, it'll show up here."
                : "Completed tasks land here."}
            </div>
          </div>
        </div>
      ) : (
        rows.map((t) => {
          const active = !isDone(t);
          const busy = busyId === t.id;
          const ticket = t.ticketId ? byTicket.get(t.ticketId) : undefined;
          let sideNote = "";
          if (t.state === "Requested" && t.remindedAt) {
            sideNote = `${t.overdue ? "Overdue. " : ""}Reminder sent ${relativeTime(t.remindedAt)}.`;
          } else if (t.state === "Requested" && t.overdue) {
            sideNote = `Overdue. ${contact} hasn't been reminded yet.`;
          } else if (t.state === "Done" && t.completedAt) {
            sideNote = `Done ${relativeTime(t.completedAt)}`;
          }
          return (
            <div key={t.id} className={"tcard" + (active ? " is-active" : "")}>
              <div className="tcard-main">
                <div className="tcard-left">
                  <div className="row-6">
                    <Tag>{KIND_LABELS[t.kind]}</Tag>
                    <Badge tone={taskTone(t.state)} dot={active}>
                      {LABELS[t.state] ?? t.state}
                    </Badge>
                    <span className="mono tcard-scope">
                      {t.projectId ? projectName(t.projectId) : ACCOUNT}
                    </span>
                    {t.overdue ? (
                      <Badge tone="danger" dot>
                        Overdue
                      </Badge>
                    ) : null}
                  </div>
                  <div className="tcard-title">{t.title}</div>
                  {t.detail ? <p className="tcard-detail">{t.detail}</p> : null}
                  <div className={"tcard-meta mono" + (t.overdue ? " is-late" : "")}>
                    {t.due ? `Due ${t.due}` : "No due date"} · Requested {t.created} by{" "}
                    {t.createdBy}
                  </div>
                  {t.documents.length > 0 ? (
                    <div className="row-8">
                      {t.documents.map((d) => (
                        <a key={d.id} className="file-chip" href={`/api/documents/${d.id}/download`}>
                          <Icon name="file-text" size={14} />
                          {d.name}
                          {d.size ? <span className="mono">{d.size}</span> : null}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="tcard-side">
                  <div className="row-8 tcard-actions">
                    {t.state === "Requested" ? (
                      <>
                        <Button
                          variant="secondary"
                          icon="bell"
                          onClick={() => void remind(t)}
                          disabled={busy}
                        >
                          Send reminder
                        </Button>
                        <Button
                          variant="secondary"
                          icon="check"
                          onClick={() => void setState(t, "Done", "Task complete.")}
                          disabled={busy}
                        >
                          Mark done
                        </Button>
                      </>
                    ) : null}
                    {t.state === "Submitted" ? (
                      <>
                        <Button
                          variant="secondary"
                          icon="rotate-ccw"
                          onClick={() =>
                            void setState(t, "Requested", `Task reopened. ${contact} gets an email.`)
                          }
                          disabled={busy}
                        >
                          Reopen
                        </Button>
                        <Button
                          icon="check"
                          onClick={() => void setState(t, "Done", "Task complete.")}
                          disabled={busy}
                        >
                          Confirm done
                        </Button>
                      </>
                    ) : null}
                  </div>
                  {sideNote ? <span className="tcard-note">{sideNote}</span> : null}
                  <div className="row-6 end">
                    <IconButton
                      icon="pencil"
                      label="Edit task"
                      variant="ghost"
                      onClick={() => setDialog({ task: t })}
                    />
                    <IconButton
                      icon="trash-2"
                      label="Delete task"
                      variant="ghost"
                      onClick={() => void del(t)}
                    />
                  </div>
                </div>
              </div>

              <div className="tcard-foot">
                <button
                  type="button"
                  className="tcard-link"
                  onClick={() => setOpenComments(openComments === t.id ? null : t.id)}
                >
                  <Icon name="message-square" size={14} />
                  {openComments === t.id
                    ? "Hide comments"
                    : t.commentCount > 0
                      ? `Comments (${t.commentCount})`
                      : "Add a comment"}
                </button>
                {t.ticketId ? (
                  <button
                    type="button"
                    className="tcard-link"
                    onClick={() => openItem("tickets", t.ticketId as string)}
                  >
                    <Icon name="link" size={14} />#{id8(t.ticketId)}
                    {ticket ? ` ${ticket.subject}` : ""} →
                  </button>
                ) : null}
              </div>
              {openComments === t.id ? (
                <TaskComments task={t} contact={contact} onPosted={reload} />
              ) : null}
            </div>
          );
        })
      )}

      {dialog && client ? (
        <TaskDialog
          clientId={client.id}
          contact={contact}
          projects={projects}
          tickets={tickets}
          defaultProjectId={inProject ? pid : null}
          task={dialog.task}
          onClose={() => setDialog(null)}
          onDelete={(t) => void del(t)}
          onSaved={() => {
            reload();
            if (!dialog.task) setTab("open");
            toast(dialog.task ? "Task updated." : `Task sent. ${contact} gets an email now.`);
          }}
        />
      ) : null}

    </div>
  );
}
